// src/Chats.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Chats.css';
import api from './api';

import backIcon from './assets/backspace-icon.png';
import deleteIcon from './assets/delete-outline-icon.png';
import goldTickIcon from './assets/goldtick-icon.png';
import ChatSkeleton from './components/skeleton/ChatSkeleton';
import DeleteChatDrawer from './components/popups/DeleteChatDrawer';
import BottomNav from './components/navigation/BottomNav';
import { useMessageUnreadContext } from './context/MessageUnreadContext';

function Chats({ currentUser }) {
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedChats, setHasLoadedChats] = useState(false);
  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);

const {
  unreadDmCount,
  refreshUnreadDmCount,
} = useMessageUnreadContext();

const [bottomNavHidden, setBottomNavHidden] = useState(false);
const scrollAnchorRef = useRef(0);

const handleChatsScroll = (event) => {
  const element = event.currentTarget;

  const maxScrollTop = Math.max(
    0,
    element.scrollHeight - element.clientHeight
  );

  const scrollTop = Math.min(
    maxScrollTop,
    Math.max(0, element.scrollTop)
  );

  const delta = scrollTop - scrollAnchorRef.current;

  if (scrollTop <= 80) {
    setBottomNavHidden(false);
    scrollAnchorRef.current = scrollTop;
    return;
  }

  if (Math.abs(delta) >= 10) {
    setBottomNavHidden(delta > 0);
    scrollAnchorRef.current = scrollTop;
  }
};

  const isAdminUser = (user) => user?.isAdmin === true || !!user?.adminRole;
  const isAppUser = (user) => user?.isApp === true || user?.accountType === 'app';
  const isSpecialUser = (user) => isAdminUser(user) || isAppUser(user);

  const getSpecialTitle = (user) => {
    if (isAdminUser(user)) return user?.adminRole || 'Admin';
    if (isAppUser(user)) return 'App account';
    return '';
  };

  useEffect(() => {
    if (!currentUser?.username || hasLoadedChats) return;

    const fetchChats = async () => {
      try {
        const res = await api.get(`/messages/chats/${currentUser.username}`);


setMessages(Array.isArray(res.data) ? res.data : []);
setHasLoadedChats(true);

refreshUnreadDmCount();

      } catch (err) {
        console.error('❌ Failed to load chats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();

}, [
  currentUser?.username,
  hasLoadedChats,
  refreshUnreadDmCount,
]);

  const formatTime = (timestamp) => {
    const msgDate = new Date(timestamp);
    const now = new Date();
    const diff = now - msgDate;
    const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) {
      return msgDate.toLocaleDateString([], { weekday: 'long' });
    }
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    if (msgDate.getFullYear() === now.getFullYear()) {
      return msgDate.toLocaleDateString([], { month: 'long', day: 'numeric' });
    }

    const yearsAgo = now.getFullYear() - msgDate.getFullYear();
    return yearsAgo === 1 ? '1 year ago' : `${yearsAgo} years ago`;
  };

  return (
    <div className="chats-container">
      <div className="chats-topbar">
        <img src={backIcon} alt="Back" className="back-icon-chats" onClick={() => navigate(-1)} />
        <h2>Chats</h2>
      </div>


<div
  className="chats-scroll-area"
  onScroll={handleChatsScroll}
>

        {loading ? (
          <ChatSkeleton />
        ) : messages.length === 0 ? (
          <p className="no-chats">No chats yet.</p>
        ) : (
          <div className="chat-list">
            {messages.map((msg, i) => (


<div
  key={msg._id || msg.otherUsername || i}
  className={`chat-item ${msg.unread > 0 ? 'unread' : ''}`}
  onClick={() => navigate(`/chat/${msg.otherUsername}`)}
>

                <img
                  src={msg.profilePic || require('./assets/placeholder-profile.png')}
                  alt="avatar"
                  className="chat-avatar"
                />

                <div className="chat-info">
                  <div className="chat-top-row">
                    <strong className="chat-name-with-dot">
                      <span className="chat-name-row">
                        <span>{msg.name}</span>

                        {isSpecialUser(msg) && (
                          <img
                            src={goldTickIcon}
                            alt={isAdminUser(msg) ? 'Admin verified' : 'App verified'}
                            className="chat-gold-tick"
                            title={getSpecialTitle(msg)}
                          />
                        )}

                        {msg.unread > 0 && <span className="chat-unread-dot" />}
                      </span>
                    </strong>

                    <span className="chat-time-inline">{formatTime(msg.timestamp)}</span>
                  </div>

                  <span
                    className="chat-last-message"
                    title={(msg.text || '').replace(/\s+/g, ' ').trim()}
                  >
                    {(msg.text || '').replace(/\s+/g, ' ').trim()}
                  </span>
                </div>

                <div className="chat-delete-wrap">
                  <img
                    src={deleteIcon}
                    alt="delete"
                    className="chat-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatToDelete(msg.otherUsername);
                      setDeleteDrawerOpen(true);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

<BottomNav
  user={currentUser}
  unreadDmCount={unreadDmCount}
  hidden={bottomNavHidden || deleteDrawerOpen}
/>

<DeleteChatDrawer
  isOpen={deleteDrawerOpen} 
       onClose={() => setDeleteDrawerOpen(false)}
        onConfirm={() => {
          if (!chatToDelete) return;

          api
            .delete(`/messages/delete/${chatToDelete}`)


.then(() => {
  setMessages((prev) =>
    prev.filter((m) => m.otherUsername !== chatToDelete)
  );

  setChatToDelete(null);
  setDeleteDrawerOpen(false);

  refreshUnreadDmCount();
})

            .catch((err) => {
              console.error('❌ Failed to delete chat:', err);
              alert('Failed to delete chat.');
            });
        }}
      />
    </div>
  );
}

export default Chats;
